import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Cpu, Globe, ShieldCheck, Zap, 
  Activity, Server, Lock, ChevronRight,
  Utensils, Stethoscope, Box
} from 'lucide-react';

const Home = ({ setPage }) => {
  const brandGold = "#ffb700";
  const canvasRef = useRef(null);
  const carouselRef = useRef(null);
  
  // --- TYPEWRITER STATE ---
  const [typewriterText, setTypewriterText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const fullText = "We Transform Operational Stress into Intelligent Systems.";

  // --- MOBILE CAROUSEL AUTO-SLIDE LOGIC ---
  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    let scrollAmount = 0;
    const slideInterval = setInterval(() => {
      // Only auto-slide if we are on a mobile screen (under 768px)
      if (window.innerWidth < 768) {
        const cardWidth = container.offsetWidth; // Width of one full card
        scrollAmount += cardWidth;
        
        // Reset to beginning if we reach the end
        if (scrollAmount >= container.scrollWidth - 10) {
          scrollAmount = 0;
        }
        
        container.scrollTo({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
    }, 4000); // Slides every 4 seconds

    return () => clearInterval(slideInterval);
  }, []);

  // --- PARTICLE NETWORK ANIMATION ---
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width, height;
    let particles = [];

    const particleCount = 40; // Reduced slightly for better mobile performance
    const connectionDistance = 150; 
    const mouseDistance = 200; 

    let mouse = { x: null, y: null };

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.5; 
        this.vy = (Math.random() - 0.5) * 0.5; 
        this.size = Math.random() * 2 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;

        if (mouse.x != null) {
          let dx = mouse.x - this.x;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < mouseDistance) {
            const force = (mouseDistance - distance) / mouseDistance;
            this.x -= (dx / distance) * force * 2; 
            this.y -= (dy / distance) * force * 2;
          }
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 183, 0, 0.5)'; 
        ctx.fill();
      }
    }

    const init = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach(p => { p.update(); p.draw(); });

      for (let a = 0; a < particles.length; a++) {
        for (let b = a; b < particles.length; b++) {
          let dx = particles[a].x - particles[b].x;
          let dy = particles[a].y - particles[b].y;
          let distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            let opacityValue = 1 - (distance / connectionDistance);
            ctx.strokeStyle = `rgba(255, 183, 0, ${opacityValue * 0.2})`; 
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.stroke();
          }
        }
      }
      requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', (e) => { mouse.x = e.x; mouse.y = e.y; });

    resize();
    init();
    animate();

    return () => window.removeEventListener('resize', resize);
  }, []);

  // --- TYPEWRITER LOGIC ---
  useEffect(() => {
    let index = 0;
    const typeInterval = setInterval(() => {
      if (index <= fullText.length) {
        setTypewriterText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(typeInterval);
        setShowCursor(false);
      }
    }, 40);
    return () => clearInterval(typeInterval);
  }, []);

  // --- FIXED STAT CARD FOR MOBILE ---
  const StatCard = ({ label, value, sub, icon: Icon, delay }) => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      className="bg-slate-900/50 border border-white/10 p-5 rounded-2xl backdrop-blur-sm hover:border-[#ffb700]/30 transition-colors group z-20 relative flex flex-col justify-between h-full"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="p-2 bg-white/5 rounded-lg text-slate-400 group-hover:text-[#ffb700] transition-colors">
          <Icon size={18} />
        </div>
        <div className="text-[10px] md:text-xs font-mono text-slate-500 uppercase tracking-wider text-right w-1/2 leading-tight">{label}</div>
      </div>
      <div className="text-2xl md:text-3xl font-black text-white mb-1">{value}</div>
      <div className="text-[10px] md:text-xs text-slate-400 border-l-2 border-[#ffb700] pl-2 leading-tight">{sub}</div>
    </motion.div>
  );

  return (
    <div className="pt-24 md:pt-32 pb-20 px-4 md:px-12 overflow-hidden bg-[#020408] min-h-screen relative selection:bg-[#ffb700] selection:text-black">
      
      <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none z-0" style={{ opacity: 0.6 }} />

      {/* ================= HERO SECTION ================= */}
      <section className="max-w-[90rem] mx-auto text-center mb-16 md:mb-20 relative z-10 flex flex-col">
        
        {/* Headline */}
        <motion.div className="order-1" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-slate-300 text-[10px] font-bold mb-6 md:mb-8 uppercase tracking-[0.2em] backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            System Operational
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6 md:mb-8 tracking-tighter text-white min-h-[5rem] md:min-h-[3.5rem] leading-[1.1]">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffb700] via-orange-400 to-[#ffb700]">
              {typewriterText}
            </span>
            <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} text-[#ffb700] transition-opacity duration-300`}>|</span>
          </h1>
        </motion.div>

        {/* CTA BUTTONS - Pushed down on mobile via mt-16 */}
        <motion.div 
          className="order-3 md:order-2 flex flex-col md:flex-row justify-center gap-3 mt-16 md:mt-0 mb-8 md:mb-24 w-full md:w-auto px-2"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
        >
        </motion.div>

        {/* ================= AUTO-SLIDING CAROUSEL (MOBILE) / GRID (DESKTOP) ================= */}
        <div 
          ref={carouselRef}
          className="order-2 md:order-3 mt-4 md:mt-0 flex overflow-x-auto snap-x snap-mandatory gap-4 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible text-left relative z-10 w-full hide-scrollbar px-2 md:px-0"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
          
          {/* 1. CLINICAL AI */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="min-h-[320px] md:min-h-0 min-w-full md:min-w-0 snap-center group relative bg-gradient-to-b from-slate-900/80 to-black/80 border border-white/10 rounded-[2rem] p-8 overflow-hidden hover:border-cyan-500/30 transition-all duration-300 flex flex-col backdrop-blur-md">
            <div className="absolute top-0 right-0 w-48 h-48 md:w-64 md:h-64 bg-cyan-500/5 blur-[60px] md:blur-[80px] rounded-full pointer-events-none" />
            
            <div className="flex items-center gap-4 mb-4 relative z-10">
              <div className="w-12 h-12 shrink-0 bg-cyan-500/10 rounded-2xl flex items-center justify-center text-cyan-400 border border-cyan-500/20">
                <Stethoscope size={24} />
              </div>
              <h4 className="text-2xl font-black text-white leading-tight">Clinical Ai</h4>
            </div>
            
            <p className="text-slate-400 leading-relaxed mb-8 text-sm flex-grow relative z-10">
              The central nervous system for healthcare. Automate patient triage, schedule via voice, and sync directly with EHRs.
            </p>
            
            <div className="flex items-center justify-between w-full mt-auto pt-5 border-t border-white/10 relative z-10">
              <div className="flex items-center gap-3">
                <a href="https://starkxaihealth.onrender.com/login" className="text-xs font-bold text-slate-300 hover:text-white transition-colors">Sign-in</a>
                <a href="https://starkxaihealth.onrender.com/signup" className="text-[10px] font-black uppercase tracking-wider bg-cyan-500 text-black px-3 py-1.5 rounded-full animate-pulse shadow-[0_0_15px_rgba(6,182,212,0.3)]">Register</a>
              </div>
              <button 
  onClick={() => window.location.href = "https://starkxaihealth.onrender.com"} 
  className="text-cyan-400 font-bold text-xs uppercase tracking-widest flex items-center gap-1"
>
  Explore <ChevronRight size={14} />
</button>
            </div>
          </motion.div>

          {/* 2. RESTAURANT AI */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="min-h-[320px] md:min-h-0 min-w-full md:min-w-0 snap-center group relative bg-gradient-to-b from-slate-900/80 to-black/80 border border-white/10 rounded-[2rem] p-8 overflow-hidden hover:border-[#ffb700]/30 transition-all duration-300 flex flex-col backdrop-blur-md">
            <div className="absolute top-0 right-0 w-48 h-48 md:w-64 md:h-64 bg-[#ffb700]/5 blur-[60px] md:blur-[80px] rounded-full pointer-events-none" />
            
            <div className="flex items-center gap-4 mb-4 relative z-10">
              <div className="w-12 h-12 shrink-0 bg-[#ffb700]/10 rounded-2xl flex items-center justify-center text-[#ffb700] border border-[#ffb700]/20">
                <Utensils size={24} />
              </div>
              <h4 className="text-2xl font-black text-white leading-tight">Restaurant Ai</h4>
            </div>
            
            <p className="text-slate-400 leading-relaxed mb-8 text-sm flex-grow relative z-10">
              AI voice receptionists, real-time inventory tracking, and bank-grade payment security. The complete OS.
            </p>
            
            <div className="flex items-center justify-between w-full mt-auto pt-5 border-t border-white/10 relative z-10">
              <div className="flex items-center gap-3">
                <a href="https://starkxrestoservices.onrender.com/login" className="text-xs font-bold text-slate-300 hover:text-white transition-colors">Sign-in</a>
                <a href="https://starkxrestoservices.onrender.com/register" className="text-[10px] font-black uppercase tracking-wider bg-[#ffb700] text-black px-3 py-1.5 rounded-full animate-pulse shadow-[0_0_15px_rgba(255,183,0,0.3)]">Register</a>
              </div>
<button 
  onClick={() => window.location.href = "https://starkxrestoservices.onrender.com"} 
  className="text-cyan-400 font-bold text-xs uppercase tracking-widest flex items-center gap-1"
>
  Explore <ChevronRight size={14} />
</button>            </div>
          </motion.div>

          {/* 3. MODEL HUB */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="min-h-[320px] md:min-h-0 min-w-full md:min-w-0 snap-center group relative bg-gradient-to-b from-slate-900/80 to-black/80 border border-white/10 rounded-[2rem] p-8 overflow-hidden hover:border-purple-500/30 transition-all duration-300 flex flex-col backdrop-blur-md">
            <div className="absolute top-0 right-0 w-48 h-48 md:w-64 md:h-64 bg-purple-500/5 blur-[60px] md:blur-[80px] rounded-full pointer-events-none" />
            
            <div className="flex items-center gap-4 mb-4 relative z-10">
              <div className="w-12 h-12 shrink-0 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-400 border border-purple-500/20">
                <Box size={24} />
              </div>
              <h4 className="text-2xl font-black text-white leading-tight">Model Hub</h4>
            </div>
            
            <p className="text-slate-400 leading-relaxed mb-8 text-sm flex-grow relative z-10">
              The unified plugin ecosystem. Enhance infrastructure with specialized modules like HIPAA Suites.
            </p>
            
            <div className="flex items-center justify-between w-full mt-auto pt-5 border-t border-white/10 relative z-10">
              <div className="flex items-center gap-3">
                <button className="text-xs font-bold text-slate-500 cursor-not-allowed">Sign-in</button>
                <div className="text-[10px] font-black uppercase tracking-wider bg-purple-500/30 text-white/50 px-3 py-1.5 rounded-full">Soon</div>
              </div>
              <button className="text-purple-400 font-bold text-xs uppercase tracking-widest flex items-center gap-1">Coming soon <ChevronRight size={14} /></button>
            </div>
          </motion.div>

        </div>
      </section>

      {/* ================= COMPANY DNA (FIXED OVERFLOW) ================= */}
      <section className="max-w-7xl mx-auto mb-20 md:mb-40 relative z-10 px-2 md:px-0">
        <div className="bg-[#0F1218] border border-white/10 rounded-[2.5rem] p-8 md:p-20 relative overflow-hidden backdrop-blur-md">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#ffb700] to-transparent opacity-50" />
          
          <div className="grid lg:grid-cols-2 gap-12 md:gap-20">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 md:mb-8 leading-tight break-words">
                We Engineer <br/> <span className="text-[#ffb700]">Invisible</span> Efficiency.
              </h2>
              <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-6">
                Stark X AI was founded on a single premise: <strong>Human potential is wasted on repetitive tasks.</strong>
              </p>
              <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-8">
                We streamline operations with 24/7 AI voice agents capable of handling unlimited concurrent calls. Engineered for high-noise environments, our system moves beyond simple listening to understand context, urgency, and intent.
              </p>
              
              <div className="flex flex-wrap justify-between md:justify-start gap-6 md:gap-12 pt-8 border-t border-white/5">
                <div>
                  <div className="text-2xl md:text-3xl font-black text-white">2026</div>
                  <div className="text-[10px] md:text-xs font-mono text-slate-500 uppercase mt-1">Established</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-black text-white">24/7</div>
                  <div className="text-[10px] md:text-xs font-mono text-slate-500 uppercase mt-1">Operational</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-black text-white">Global</div>
                  <div className="text-[10px] md:text-xs font-mono text-slate-500 uppercase mt-1">Deployment</div>
                </div>
              </div>
            </div>

            <div className="space-y-8 md:space-y-6">
              {[
                { title: "Sovereign Architecture", desc: "We own our stack. From the telephony layer to the neural processing unit, we don't rely on third-party wrappers.", icon: Server },
                { title: "Hyper-Local Focus", desc: "Our AI is trained on local dialects, menu items, and medical terminologies specific to the regions we serve.", icon: Globe },
                { title: "Rapid Deployment", desc: "We can onboard a new clinic or restaurant franchise in under 48 hours with our modular API system.", icon: Zap }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 md:gap-6 group">
                  <div className="mt-1 shrink-0">
                    <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center text-slate-400 group-hover:text-[#ffb700] group-hover:bg-[#ffb700]/10 transition-colors">
                      <item.icon size={24} />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">{item.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= TECHNICAL SPECS ================= */}
      <section className="max-w-7xl mx-auto mb-20 md:mb-40 border-y border-white/5 py-16 md:py-20 bg-black/20 relative z-10 px-2 md:px-0">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <div className="inline-block px-4 py-1.5 rounded-full bg-slate-800 text-slate-400 text-[10px] font-bold mb-6 uppercase tracking-widest">
              <Cpu size={12} className="inline mr-2" /> System Architecture
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
              Built for <span className="text-[#ffb700]">Speed</span> & <br/>
              Military Security.
            </h2>
            <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-8">
              We don't just run chatbots; we run mission-critical infrastructure. 
              Engineered for <span className="text-white font-bold">zero-latency response</span> and absolute data sovereignty.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-slate-300 text-sm font-bold bg-white/5 px-4 py-2 rounded-xl border border-white/5">
                <ShieldCheck size={18} className="text-green-500" /> SOC 2 Compliant
              </div>
              <div className="flex items-center gap-2 text-slate-300 text-sm font-bold bg-white/5 px-4 py-2 rounded-xl border border-white/5">
                <Lock size={18} className="text-[#ffb700]" /> Encrypted
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <StatCard label="Latency" value="~200ms" sub="Real-time Voice" icon={Zap} delay={0} />
            <StatCard label="Uptime" value="99.99%" sub="Redundant Nodes" icon={Activity} delay={0.1} />
            <StatCard label="Security" value="AES-256" sub="Military-Grade" icon={Lock} delay={0.2} />
            <StatCard label="Agents" value="10k+" sub="Concurrent AI" icon={Server} delay={0.3} />
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;